import { Action, createSelector, Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { UserPrivilegeEnum } from '../../../../enums/rights/user-privilege.enum';
import { ResetStateDefaults } from '../../../base-action-class.model';
import { RFavorite } from './r-favorite.action';
import { RightsScopeEnum } from '../../../../enums/rights/rights-scope.enum';

interface RFavoriteXsStateModel {
  viewGlobalFavorite: string;
  createGlobalFavorite: string;
  editGlobalFavorite: string;
  deleteGlobalFavorite: string;
  changePrioOfFavorite: string;

  viewOrgFavorite: string;
  createOrgFavorite: string;
  editOrgFavorite: string;
  deleteOrgFavorite: string;
}

const getDefaults = (): RFavoriteXsStateModel => ({
  viewGlobalFavorite: null,
  createGlobalFavorite: null,
  editGlobalFavorite: null,
  deleteGlobalFavorite: null,
  changePrioOfFavorite: null,

  viewOrgFavorite: null,
  createOrgFavorite: null,
  editOrgFavorite: null,
  deleteOrgFavorite: null
});

@State<RFavoriteXsStateModel>({
  name: 'rFavorite',
  defaults: getDefaults()
})
@Injectable()
export class RFavoriteXsState {

  //region SELECTOR

  @Selector()
  static canViewAnyFavorite(state: RFavoriteXsStateModel): boolean {
    return state.viewGlobalFavorite != null
      || state.viewOrgFavorite != null;
  }

  @Selector()
  static canCreateAnyFavorite(state: RFavoriteXsStateModel): boolean {
    return state.createGlobalFavorite != null
      || state.createOrgFavorite != null;
  }

  @Selector()
  static canEditAnyFavorite(state: RFavoriteXsStateModel): boolean {
    return state.editGlobalFavorite != null
      || state.editOrgFavorite != null;
  }

  @Selector()
  static canDeleteAnyFavorite(state: RFavoriteXsStateModel): boolean {
    return state.deleteGlobalFavorite != null
      || state.deleteOrgFavorite != null;
  }

  @Selector()
  static canChangePrioOfFavorite(state: RFavoriteXsStateModel): boolean {
    return state.changePrioOfFavorite != null;
  }

  static canViewFavorite(scope: RightsScopeEnum) {
    return createSelector([RFavoriteXsState], (state: RFavoriteXsStateModel): boolean => {
      switch (scope) {

        case RightsScopeEnum.ORGANIZATION:
          return state.viewOrgFavorite != null;
        case RightsScopeEnum.GLOBAL:
          return state.viewGlobalFavorite != null;
      }
      return false;
    });
  }

  static canCreateFavorite(scope: RightsScopeEnum) {
    return createSelector([RFavoriteXsState], (state: RFavoriteXsStateModel): boolean => {
      switch (scope) {

        case RightsScopeEnum.ORGANIZATION:
          return state.createOrgFavorite != null;
        case RightsScopeEnum.GLOBAL:
          return state.createGlobalFavorite != null;
      }
      return false;
    });
  }

  static canEditFavorite(scope: RightsScopeEnum) {
    return createSelector([RFavoriteXsState], (state: RFavoriteXsStateModel): boolean => {
      switch (scope) {

        case RightsScopeEnum.ORGANIZATION:
          return state.editOrgFavorite != null;
        case RightsScopeEnum.GLOBAL:
          return state.editGlobalFavorite != null;
      }
      return false;
    });
  }

  static canDeleteFavorite(scope: RightsScopeEnum) {
    return createSelector([RFavoriteXsState], (state: RFavoriteXsStateModel): boolean => {
      switch (scope) {

        case RightsScopeEnum.ORGANIZATION:
          return state.deleteOrgFavorite != null;
        case RightsScopeEnum.GLOBAL:
          return state.deleteGlobalFavorite != null;
      }
      return false;
    });
  }

  //endregion

  //region ACTIONS
  @Action(RFavorite.SetFavoritePrivileges)
  public setFavoritePrivileges(ctx: StateContext<RFavoriteXsStateModel>, action: RFavorite.SetFavoritePrivileges) {
    const state = ctx.getState();
    const favoritePrivileges = [...action.activeUserRightInfoDtoModel.favoritePrivileges];

    //find user privilege data model
    const viewGlobalFavorite = favoritePrivileges.find(id => id === UserPrivilegeEnum.VIEW_SYSTEM_FAVORITE);
    const createGlobalFavorite = favoritePrivileges.find(id => id === UserPrivilegeEnum.CREATE_SYSTEM_FAVORITE);
    const editGlobalFavorite = favoritePrivileges.find(id => id === UserPrivilegeEnum.EDIT_SYSTEM_FAVORITE);
    const deleteGlobalFavorite = favoritePrivileges.find(id => id === UserPrivilegeEnum.DELETE_SYSTEM_FAVORITE);
    const changePrioOfFavorite = favoritePrivileges.find(id => id === UserPrivilegeEnum.CHANGE_PRIO_OF_OBJECT_FAVORITE);

    const viewOrgFavorite = favoritePrivileges.find(id => id === UserPrivilegeEnum.VIEW_ORGANIZATION_FAVORITE);
    const createOrgFavorite = favoritePrivileges.find(id => id === UserPrivilegeEnum.CREATE_ORGANIZATION_FAVORITE);
    const editOrgFavorite = favoritePrivileges.find(id => id === UserPrivilegeEnum.EDIT_ORGANIZATION_FAVORITE);
    const deleteOrgFavorite = favoritePrivileges.find(id => id === UserPrivilegeEnum.DELETE_ORGANIZATION_FAVORITE);

    //set the state
    ctx.setState({
      ...state,
      viewGlobalFavorite: viewGlobalFavorite,
      createGlobalFavorite: createGlobalFavorite,
      editGlobalFavorite: editGlobalFavorite,
      deleteGlobalFavorite: deleteGlobalFavorite,
      changePrioOfFavorite: changePrioOfFavorite,

      viewOrgFavorite: viewOrgFavorite,
      createOrgFavorite: createOrgFavorite,
      editOrgFavorite: editOrgFavorite,
      deleteOrgFavorite: deleteOrgFavorite
    });
  }

  @Action(ResetStateDefaults)
  public resetDefaults(ctx: StateContext<RFavoriteXsStateModel>): void {
    ctx.setState({ ...getDefaults() });
  }

  //endregion
}
